import { useState } from 'react';
import SearchByVehicleForm from './SearchByVehicleForm';
import MobileDrawer from '@c/drawers/MobileDrawer';
import type { UserBike } from 'models/shared';
import { UserDocument } from 'models/user';
import SafeImage from '@ui/SafeImage';
import { prefix } from '@util/imagePrefix';
import InfoIcon from '@c/icons/info';
import { TemporaryBike } from 'context/BikeFilterContext';
import { addUserBike } from '@util/firestore/users';
import { useQueryClient } from '@tanstack/react-query';

type MobileSearchByVehicleProps = {
  show: boolean;
  dismiss: () => void;
  onBikeComplete: (bike: UserBike) => void;
  onClear?: () => void;
  variant?: 'add-to-garage' | 'filter';
  initialBike?: Partial<UserBike> | null;
  userDoc?: UserDocument | null | undefined;
  hideGarage?: boolean;
  skipConfirmation?: boolean;
};

export default function MobileSearchByVehicle({
  show,
  dismiss,
  onBikeComplete,
  onClear,
  variant = 'filter',
  initialBike,
  userDoc,
  hideGarage = false,
  skipConfirmation = false,
}: MobileSearchByVehicleProps) {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [temporaryBike, setTemporaryBike] = useState<TemporaryBike | null>(
    null
  );
  const [formPage, setFormPage] = useState<'search' | 'confirm-add-to-garage'>(
    'search'
  );
  const [prevShow, setPrevShow] = useState(show);

  const hasSelectedBike = Boolean(
    initialBike?.year && initialBike?.make && initialBike?.model
  );
  const showClear =
    variant === 'filter' &&
    !!onClear &&
    hasSelectedBike &&
    formPage === 'search';

  const parsedBrandName = temporaryBike?.make
    ?.split(' ')
    .join('-')
    .toLowerCase();
  const brandImage = `${prefix}/assets/brands/${parsedBrandName}_1920x2560.png`;

  if (show !== prevShow) {
    setPrevShow(show);
    if (show) {
      setSearchQuery('');
      setFormPage('search');
      setTemporaryBike(null);
    }
  }

  const onSearchComplete = (bike: UserBike, fromGarage?: boolean) => {
    if ((fromGarage && variant === 'filter') || skipConfirmation) {
      onBikeComplete(bike);
      dismiss();
      return;
    }

    setFormPage('confirm-add-to-garage');
    setTemporaryBike(bike);
  };

  const getDrawerTitle = () => {
    if (variant === 'add-to-garage') {
      if (formPage === 'confirm-add-to-garage') {
        return 'Confirm Vehicle';
      }

      return 'Add Vehicle';
    }
    return 'Find Your Vehicle';
  };

  return (
    <MobileDrawer
      title={getDrawerTitle()}
      show={show}
      dismiss={dismiss}
      headerExtraButton={
        formPage === 'confirm-add-to-garage' ? (
          <button
            type="button"
            className="text-[2.1rem] font-medium text-brand-secondary"
            onClick={() => {
              setFormPage('search');
            }}
          >
            Back
          </button>
        ) : showClear ? (
          <button
            type="button"
            className="text-[2.1rem] font-medium text-brand-secondary"
            onClick={() => {
              onClear?.();
              dismiss();
            }}
          >
            Clear
          </button>
        ) : undefined
      }
    >
      <div className="flex h-[60rem] w-full flex-col gap-y-6 px-[2rem] py-[3rem]">
        {formPage === 'search' ? (
          <SearchByVehicleForm
            onBikeComplete={onSearchComplete}
            onDismiss={dismiss}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            requireConfirmation={true}
            temporaryBike={temporaryBike}
            setTemporaryBike={setTemporaryBike}
            hideGarage={hideGarage}
          />
        ) : (
          <div className="flex h-full flex-col items-start justify-between">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col items-start gap-4">
                <div className="flex items-center gap-4">
                  <SafeImage
                    src={brandImage}
                    alt={temporaryBike?.make ?? ''}
                    className="h-24 w-24 object-contain"
                    fallback={
                      <div className="flex h-[7rem] w-[7rem] items-center justify-center rounded-[0.5rem] bg-zinc-200 p-1 text-center text-[1rem] font-medium text-zinc-600">
                        {temporaryBike?.make}
                      </div>
                    }
                  />

                  <h3 className="text-[2.1rem] font-semibold text-black">
                    {temporaryBike?.year} {temporaryBike?.make}{' '}
                    {temporaryBike?.model}
                  </h3>
                </div>

                <div className="flex items-center gap-4">
                  <InfoIcon className="h-10 w-10 text-brand-secondary" />

                  <span className="text-medium text-[1.6rem] font-semibold text-zinc-700">
                    {userDoc
                      ? userDoc.bikes?.some(
                          (b) =>
                            b.make === temporaryBike?.make &&
                            b.model === temporaryBike?.model &&
                            b.year === temporaryBike?.year
                        )
                        ? 'This vehicle is already in your garage.'
                        : 'This vehicle will be added to your garage.'
                      : 'Sign in to add this vehicle to your garage.'}
                  </span>
                </div>
              </div>
            </div>

            <button
              type="button"
              className="h-[5.5rem] w-full rounded-[2rem] bg-brand-secondary px-4 py-2 text-[1.8rem] font-semibold text-white"
              onClick={() => {
                if (temporaryBike) {
                  onBikeComplete(temporaryBike as UserBike);
                }
                // add to garage if not already in garage
                if (
                  temporaryBike &&
                  userDoc?.uid &&
                  !userDoc?.bikes?.some(
                    (b) =>
                      b.make === temporaryBike.make &&
                      b.model === temporaryBike.model &&
                      b.year === temporaryBike.year
                  )
                ) {
                  addUserBike(userDoc.uid, temporaryBike as UserBike);
                }

                setFormPage('search');
                dismiss();
              }}
            >
              Show results
            </button>
          </div>
        )}
      </div>
    </MobileDrawer>
  );
}
